import { getRoomsSetByCount } from "../LDSHelpers";
import { getDepRetLegsFromLinkedFlights } from "../vacationPackagesLinkedFlights";
import { buildSelectedFlightOptionFromUser } from "../vacationPackageFlightSelectionSSOT";
import { normalizeLdsExtendedDealRows } from "./normalizeLdsExtendedDealRows";

export const LDS_MULTI_ROOM_COMBO_DEAL_ID_MISSING = "LDS_MULTI_ROOM_COMBO_DEAL_ID_MISSING";

export const LDS_MULTI_ROOM_COMBO_DEAL_ID_USER_MESSAGE_HE =
  "חסר מזהה חדר+טיסה (combo) לכל חדר בהזמנה מרובת חדרים. יש לבחור מחדש חדרים וטיסה.";

function pickToken(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    const v = row[key];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

function roomInner(slot) {
  if (!slot || typeof slot !== "object") return {};
  const inner =
    slot.room && typeof slot.room === "object" ? { ...slot.room } : { ...slot };
  if (inner["@seqNum"] == null && slot["@seqNum"] != null) {
    inner["@seqNum"] = slot["@seqNum"];
  }
  if (
    !pickToken(inner, ["roomSet", "RoomSet", "room_set"]) &&
    pickToken(slot, ["roomSet", "RoomSet", "room_set"])
  ) {
    inner.roomSet = pickToken(slot, ["roomSet", "RoomSet", "room_set"]);
  }
  return inner;
}

function parseComboTokenObject(token) {
  const raw = String(token || "").trim();
  if (!raw || /^\d{8,}$/.test(raw)) return null;
  try {
    if (raw.startsWith("{") || raw.startsWith("[")) {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    }
  } catch {
    /* ignore */
  }
  try {
    let b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const bin = typeof atob === "function" ? atob(b64) : "";
    if (!bin) return null;
    const parsed = JSON.parse(bin);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function isRoomFlightComboToken(token) {
  const decoded = parseComboTokenObject(token);
  if (!decoded || typeof decoded !== "object") return false;
  const roomSet = decoded.roomSet ?? decoded.RoomSet ?? decoded.room_set;
  const roomType = decoded.roomType ?? decoded.roomTypeName ?? decoded.roomName;
  const hasRoom = Boolean(String(roomSet || roomType || "").trim());
  const flightLegs = decoded.flightLegs;
  const hasFlight =
    (Array.isArray(flightLegs) && flightLegs.length > 0) ||
    Boolean(decoded.outboundFlight || decoded.inboundFlight);
  return hasRoom && hasFlight;
}

/** Explicit JSON combo rebuilt for multi-room submit (not LDS JWT package token). */
function isExplicitJsonRoomComboToken(token) {
  const raw = String(token || "").trim();
  if (!raw.startsWith("{")) return false;
  return isRoomFlightComboToken(token);
}

function isLdsJwtPackageDealToken(token) {
  const raw = String(token || "").trim();
  if (!raw.startsWith("eyJ")) return false;
  return isRoomFlightComboToken(token);
}

function tokenEqualsGlobalFlightDealId(token, globalFlightDealId) {
  return Boolean(
    globalFlightDealId && token && String(token).trim() === String(globalFlightDealId).trim()
  );
}

function resolveGlobalFlightLdsDealId(context = {}) {
  const linked =
    context.linkedFlights ??
    context.selectedFlightOptionFromUser?.linkedFlights ??
    context.selectedResult?.linkedFlights ??
    null;
  // Match BE _global_linkedflights_lds_deal_id: order linkedFlights.ldsDealId is authoritative.
  const fromLinked = pickToken(linked, ["ldsDealId", "dealId"]);
  if (fromLinked) return fromLinked;
  return (
    pickToken(context.selectedFlightOptionFromUser, ["ldsDealId"]) ||
    pickToken(context.selectedResult, ["ldsDealId"]) ||
    ""
  );
}

function flattenExtendedDealRows(extended) {
  return normalizeLdsExtendedDealRows(extended);
}

/**
 * SSOT: extended room rows for multi-room combo — API matrix, then deal snapshot fallbacks.
 * @param {object} [options]
 * @returns {{ rows: object[], sourcePath: string|null }}
 */
export function resolveExtendedDealRowsForLdsMultiRoom(options = {}) {
  const candidates = [
    ["extendedDealApiData", options.extendedDealApiData],
    ["selectedResult.extended", options.selectedResult?.extended],
    ["selectedResult.fullDeal.extendedDeal", options.selectedResult?.fullDeal?.extendedDeal],
    ["selectedResult.fullDeal.extended", options.selectedResult?.fullDeal?.extended],
    ["selectedResult.shortenedDeal", options.selectedResult?.shortenedDeal],
    ["selectedResult.fullDeal.shortenedDeal", options.selectedResult?.fullDeal?.shortenedDeal],
  ];
  for (const [sourcePath, raw] of candidates) {
    const rows = normalizeLdsExtendedDealRows(raw);
    if (rows.length) {
      return { rows, sourcePath };
    }
  }
  return { rows: [], sourcePath: null };
}

function extendedRowsFromOptions(options = {}) {
  if (Array.isArray(options._resolvedExtendedRows)) {
    return options._resolvedExtendedRows;
  }
  const { rows } = resolveExtendedDealRowsForLdsMultiRoom(options);
  return rows;
}

function collectAvailableDealIds(extendedRows) {
  const ids = [];
  for (const row of extendedRows) {
    const tok = pickToken(row, ["ldsDealId", "LdsDealId", "dealToken", "dealId"]);
    if (tok) ids.push(tok);
  }
  return [...new Set(ids)];
}

function normalizeLeg(legLike) {
  if (!legLike) return null;
  if (Array.isArray(legLike)) return legLike[0] || null;
  return legLike;
}

function resolveSnapshotLegs(selectedFlightOptionFromUser, linkedFlightsFallback = null) {
  if (!selectedFlightOptionFromUser || typeof selectedFlightOptionFromUser !== "object") {
    selectedFlightOptionFromUser = linkedFlightsFallback
      ? { linkedFlights: linkedFlightsFallback }
      : null;
  }
  if (!selectedFlightOptionFromUser) {
    return { outbound: null, inbound: null };
  }
  let outbound = normalizeLeg(selectedFlightOptionFromUser.outboundFlight);
  let inbound = normalizeLeg(selectedFlightOptionFromUser.inboundFlight);
  const linked =
    selectedFlightOptionFromUser.linkedFlights ?? linkedFlightsFallback ?? null;
  if (linked && typeof linked === "object") {
    const flights = Array.isArray(linked.flight)
      ? linked.flight
      : linked.flight
        ? [linked.flight]
        : [];
    if (!outbound && flights[0]) {
      const node = flights[0];
      outbound = normalizeLeg(node?.leg ?? node);
    }
    if (!inbound && flights[1]) {
      const node = flights[1];
      inbound = normalizeLeg(node?.leg ?? node);
    }
    if (!outbound || !inbound) {
      const { depFlight, retFlight } = getDepRetLegsFromLinkedFlights(linked);
      if (!outbound && depFlight) outbound = depFlight;
      if (!inbound && retFlight) inbound = retFlight;
    }
  }
  return { outbound, inbound };
}

export function coalesceSelectedFlightOption(options = {}) {
  let selected = options.selectedFlightOptionFromUser;
  const linked =
    options.linkedFlights ??
    selected?.linkedFlights ??
    options.selectedResult?.linkedFlights ??
    null;

  const legs = resolveSnapshotLegs(selected, linked);
  if (legs.outbound && legs.inbound) {
    return selected || { linkedFlights: linked };
  }

  if (options.selectedResult) {
    const built = buildSelectedFlightOptionFromUser({
      selectedResult: options.selectedResult,
      selectedFlight: linked,
      selectedFlightIndex: options.selectedResult?.selectedFlightIndex,
      source: selected?.source ?? "default_first",
      linkedFlightsPool: options.selectedResult?.linkedFlights,
    });
    if (built) {
      const builtLegs = resolveSnapshotLegs(built, built.linkedFlights);
      if (builtLegs.outbound && builtLegs.inbound) return built;
    }
  }

  if (linked && typeof linked === "object") {
    const synthetic = {
      ...(selected && typeof selected === "object" ? selected : {}),
      linkedFlights: linked,
    };
    const synLegs = resolveSnapshotLegs(synthetic, linked);
    if (synLegs.outbound && synLegs.inbound) return synthetic;
  }

  return selected;
}

function resolveRoomFieldsForCombo(slot, roomIndex, extendedRows) {
  const inner = roomInner(slot);
  const merged = { ...(inner && typeof inner === "object" ? inner : {}) };

  if (merged["@seqNum"] == null && slot?.["@seqNum"] != null) {
    merged["@seqNum"] = slot["@seqNum"];
  }
  if (!pickToken(merged, ["roomType", "roomTypeName", "roomName"]) && slot?.roomType) {
    merged.roomType = slot.roomType;
  }

  let roomSet = pickToken(merged, ["roomSet", "RoomSet", "room_set"]);
  if (!roomSet) {
    const adults = Array.isArray(slot?.adults) ? slot.adults.length : 0;
    const kids = Array.isArray(slot?.kids) ? slot.kids.length : 0;
    const candidates = getRoomsSetByCount(adults, kids);
    const extRow = findExtendedRowForRoom(extendedRows, merged, roomIndex);
    const extSet = extRow ? pickToken(extRow, ["roomSet", "RoomSet", "room_set"]) : "";
    if (extSet && candidates.includes(extSet)) {
      roomSet = extSet;
      merged._roomSetSource = "extendedDeal.rowBySeqOrOccupancy";
    } else if (candidates.length) {
      roomSet = candidates[0];
      merged._roomSetSource = "occupancy.getRoomsSetByCount[0]";
    }
  } else {
    merged._roomSetSource = "slot.room.roomSet";
  }
  if (roomSet) merged.roomSet = roomSet;
  return merged;
}

function writeComboRoomToSlot(slot, roomFields) {
  const token = pickToken(roomFields, ["ldsDealId", "dealId"]);
  if (!token) return slot;
  const nextRoom = {
    ...(slot?.room && typeof slot.room === "object" ? slot.room : {}),
    ...roomFields,
    ldsDealId: token,
    dealId: roomFields.dealId || token,
  };
  return { ...slot, room: nextRoom };
}

function legToComboFlightLeg(leg) {
  if (!leg || typeof leg !== "object") return null;
  return {
    flightNumber: leg.flightNumber ?? leg.fltNo ?? leg.number ?? "",
    cityCodeDep:
      leg.cityCodeDep ??
      leg.depCity ??
      leg.depAirport ??
      leg.origin ??
      leg.from ??
      "",
    cityCodeArr:
      leg.cityCodeArr ??
      leg.arrCity ??
      leg.arrAirport ??
      leg.destination ??
      leg.to ??
      "",
    depDate: leg.depDate ?? leg.departureDate ?? "",
    depTime: leg.depTime ?? "",
    arrDate: leg.arrDate ?? leg.arrivalDate ?? "",
    arrTime: leg.arrTime ?? "",
    airline:
      leg.airline ??
      leg.airlineId ??
      leg.airLine ??
      leg.marketingAirline ??
      leg.carrier ??
      "",
  };
}

/** Mirror BE _rebuild_room_combo_token_from_selection for FE submit SSOT. */
export function rebuildRoomComboLdsDealIdFromFlightSelection(
  selectedFlightOptionFromUser,
  room,
  options = {}
) {
  const linked =
    options.linkedFlights ??
    selectedFlightOptionFromUser?.linkedFlights ??
    options.selectedResult?.linkedFlights ??
    null;
  const { outbound, inbound } = resolveSnapshotLegs(
    selectedFlightOptionFromUser,
    linked
  );
  const roomSet = pickToken(room, ["roomSet", "RoomSet", "room_set"]);
  if (!roomSet || !outbound || !inbound) return null;
  const roomType =
    pickToken(room, ["roomType", "roomTypeName", "roomName"]) || "ROOM";
  return JSON.stringify({
    roomSet,
    roomType,
    flightLegs: [legToComboFlightLeg(outbound), legToComboFlightLeg(inbound)],
  });
}

/** Resolve per-room combo token from extended rows or flight selection rebuild. */
export function resolvePerRoomComboLdsDealId(slot, roomIndex, options = {}) {
  const globalFlightDealId = resolveGlobalFlightLdsDealId(options);
  const extendedRows = extendedRowsFromOptions(options);
  const selectedFlight = coalesceSelectedFlightOption(options);
  const room = resolveRoomFieldsForCombo(slot, roomIndex, extendedRows);
  const existing = pickToken(room, ["ldsDealId", "dealId"]);

  // Keep an already-built room+flight JSON combo. Numeric / JWT / opaque room
  // tokens are valid for single-room SOAP but multi-room submit must rebuild a
  // per-room combo — otherwise validation blocks the order.
  if (
    existing &&
    !tokenEqualsGlobalFlightDealId(existing, globalFlightDealId) &&
    isExplicitJsonRoomComboToken(existing)
  ) {
    return existing;
  }

  const extRow = findExtendedRowForRoom(extendedRows, room, roomIndex);
  const fromExtended = extRow
    ? pickExtendedPerRoomComboToken(extRow, globalFlightDealId)
    : null;
  if (fromExtended) return fromExtended;

  const rebuilt = rebuildRoomComboLdsDealIdFromFlightSelection(
    selectedFlight,
    room,
    options
  );
  if (rebuilt) return rebuilt;

  return null;
}

function pickExtendedPerRoomComboToken(row, globalFlightDealId) {
  const tok = pickToken(row, ["ldsDealId", "LdsDealId", "dealToken", "dealId"]);
  if (!tok || tokenEqualsGlobalFlightDealId(tok, globalFlightDealId)) return null;
  // LDS extended rows often carry package JWT — submit SSOT is rebuilt JSON combo.
  if (isExplicitJsonRoomComboToken(tok)) return tok;
  return null;
}

function pickValidComboToken(row, globalFlightDealId) {
  return pickExtendedPerRoomComboToken(row, globalFlightDealId);
}

function pickUniqueRoomTypeMatch(candidates, roomType, roomIndex = 0) {
  if (!roomType || !Array.isArray(candidates) || !candidates.length) return null;
  const exact = candidates.filter((row) => {
    const rt = pickToken(row, ["roomType", "roomTypeName", "roomName"]).toUpperCase();
    return rt === roomType;
  });
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) {
    return exact[roomIndex] ?? null;
  }
  const GENERIC = new Set(["ROOM", "TWIN", "DBL", "DOUBLE", "SGL", "SINGLE", "TRIPLE", "QUAD"]);
  const fuzzy = candidates.filter((row) => {
    const rt = pickToken(row, ["roomType", "roomTypeName", "roomName"]).toUpperCase();
    if (!rt) return false;
    if (rt === roomType) return true;
    if (GENERIC.has(roomType)) return false;
    return rt.includes(roomType) || roomType.includes(rt);
  });
  if (fuzzy.length === 1) return fuzzy[0];
  if (fuzzy.length > 1) return fuzzy[roomIndex] ?? null;
  return null;
}

export function findExtendedRowForRoom(extendedRows, room, roomIndex) {
  const rows = Array.isArray(extendedRows) ? extendedRows : [];
  const seq = room["@seqNum"] ?? room.seqNum ?? room.roomId ?? room.RoomId;
  if (seq != null) {
    const hit = rows.find((row) => {
      const rowSeq = row["@seqNum"] ?? row.seqNum ?? row.roomId ?? row.RoomId;
      return rowSeq != null && String(rowSeq) === String(seq);
    });
    if (hit) return hit;
  }
  const roomSet = pickToken(room, ["roomSet", "RoomSet"]).toUpperCase();
  const roomType = pickToken(room, ["roomType", "roomTypeName", "roomName"]).toUpperCase();
  if (roomSet) {
    const sameSet = rows.filter(
      (row) => pickToken(row, ["roomSet", "RoomSet"]).toUpperCase() === roomSet
    );
    if (!sameSet.length) return null;
    if (roomType) {
      return pickUniqueRoomTypeMatch(sameSet, roomType, roomIndex);
    }
    if (sameSet.length === 1) return sameSet[0];
    const types = new Set(
      sameSet
        .map((row) => pickToken(row, ["roomType", "roomTypeName", "roomName"]).toUpperCase())
        .filter(Boolean)
    );
    if (types.size <= 1 && sameSet[roomIndex] != null) return sameSet[roomIndex];
    return null;
  }
  if (roomType) {
    return pickUniqueRoomTypeMatch(rows, roomType, roomIndex);
  }
  return rows.length === 1 ? rows[0] : null;
}

function resolveComboTokenSourcePath(slot, roomIndex, options, resolvedToken) {
  const globalFlightDealId = resolveGlobalFlightLdsDealId(options);
  const extendedRows = extendedRowsFromOptions(options);
  const room = resolveRoomFieldsForCombo(slot, roomIndex, extendedRows);
  const existing = pickToken(room, ["ldsDealId", "dealId"]);
  if (
    existing &&
    resolvedToken &&
    existing === resolvedToken &&
    !tokenEqualsGlobalFlightDealId(existing, globalFlightDealId) &&
    isExplicitJsonRoomComboToken(existing)
  ) {
    return "slot.room.ldsDealId.explicitJsonCombo";
  }
  const extRow = findExtendedRowForRoom(extendedRows, room, roomIndex);
  const fromExtended = extRow
    ? pickExtendedPerRoomComboToken(extRow, globalFlightDealId)
    : null;
  if (fromExtended && fromExtended === resolvedToken) {
    return "extendedDealApiData.row.ldsDealId";
  }
  if (resolvedToken && isExplicitJsonRoomComboToken(resolvedToken)) {
    return "rebuildRoomComboLdsDealIdFromFlightSelection";
  }
  return "unknown";
}

/**
 * TRACE before multi-room combo validation / LDS submit.
 * @param {unknown} roomsAndPaxInfo
 * @param {object} options
 */
export function traceLdsMultiRoomComboPayload(roomsAndPaxInfo, options = {}) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  const packageDealId =
    options.selectedResult?.dealId ??
    options.selectedResult?.fullDeal?.dealId ??
    options.selectedResult?.id ??
    null;
  const extendedRows = extendedRowsFromOptions(options);
  const hydrationOptions = {
    ...options,
    selectedFlightOptionFromUser: coalesceSelectedFlightOption(options),
  };

  const roomTraces = rooms.map((slot, roomIndex) => {
    const inner = roomInner(slot);
    const roomFields = resolveRoomFieldsForCombo(slot, roomIndex, extendedRows);
    const resolvedCombo = resolvePerRoomComboLdsDealId(slot, roomIndex, hydrationOptions);
    const extRow = findExtendedRowForRoom(extendedRows, roomFields, roomIndex);
    return {
      roomIndex,
      packageDealId: packageDealId != null ? String(packageDealId) : null,
      roomName:
        pickToken(inner, ["roomType", "roomTypeName", "roomName"]) ||
        pickToken(slot, ["roomType", "roomName"]) ||
        null,
      roomIdentifier:
        inner["@seqNum"] ??
        inner.seqNum ??
        inner.roomId ??
        inner.RoomId ??
        slot?.["@seqNum"] ??
        null,
      roomType: pickToken(roomFields, ["roomType", "roomTypeName", "roomName"]) || null,
      roomSet: pickToken(roomFields, ["roomSet", "RoomSet", "room_set"]) || null,
      roomSetSource: roomFields._roomSetSource ?? null,
      token: pickToken(inner, ["ldsDealId", "dealId"]) || null,
      dealToken: pickToken(inner, ["ldsDealId", "dealId", "dealToken"]) || null,
      resolvedComboToken: resolvedCombo || null,
      resolvedComboSource: resolveComboTokenSourcePath(
        slot,
        roomIndex,
        hydrationOptions,
        resolvedCombo
      ),
      extendedRowSeq:
        extRow?.["@seqNum"] ?? extRow?.seqNum ?? extRow?.roomId ?? null,
      extendedRowLdsDealId: extRow
        ? pickToken(extRow, ["ldsDealId", "LdsDealId", "dealToken", "dealId"])
        : null,
      extendedRowMatchPath: extRow ? "extendedDealApiData.findExtendedRowForRoom" : null,
      adults: Array.isArray(slot?.adults) ? slot.adults.length : 0,
      kids: Array.isArray(slot?.kids) ? slot.kids.length : 0,
    };
  });

  const payload = {
    tag: "LDS_MULTI_ROOM_COMBO_TRACE",
    context: options.context ?? null,
    packageDealId: packageDealId != null ? String(packageDealId) : null,
    roomCount: rooms.length,
    extendedRowCount: extendedRows.length,
    globalFlightDealId: resolveGlobalFlightLdsDealId(options) || null,
    rooms: roomTraces,
  };

  try {
    // eslint-disable-next-line no-console
    console.info("[LDS_MULTI_ROOM_COMBO_TRACE]", payload);
  } catch {
    /* ignore */
  }

  return payload;
}

/**
 * Fill per-room combo ldsDealId from extendedDeal rows or rebuild from flight selection.
 */
export function hydrateMultiRoomComboLdsDealIdsFromExtended(
  roomsAndPaxInfo,
  options = {}
) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  if (rooms.length < 2) return rooms;

  const hydrationOptions = {
    ...options,
    selectedFlightOptionFromUser: coalesceSelectedFlightOption(options),
  };

  return rooms.map((slot, roomIndex) => {
    if (!slot || typeof slot !== "object") return slot;
    const combo = resolvePerRoomComboLdsDealId(slot, roomIndex, hydrationOptions);
    if (!combo) return slot;
    const roomFields = resolveRoomFieldsForCombo(
      slot,
      roomIndex,
      extendedRowsFromOptions(hydrationOptions)
    );
    roomFields.ldsDealId = combo;
    roomFields.dealId = combo;
    return writeComboRoomToSlot(slot, roomFields);
  });
}

/**
 * FlightsHotel multi-room SSOT: each room slot must carry its own combo ldsDealId
 * (never the global linkedFlights.ldsDealId used for flight-only context).
 *
 * @param {unknown} roomsAndPaxInfo
 * @param {{
 *   linkedFlights?: object|null,
 *   selectedFlightOptionFromUser?: object|null,
 *   selectedResult?: object|null,
 *   extendedDealApiData?: unknown,
 *   context?: string,
 * }} [options]
 */
export function validateLdsMultiRoomComboDealIds(roomsAndPaxInfo, options = {}) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  if (rooms.length < 2) {
    return { ok: true, code: null, traces: [], issues: [] };
  }

  traceLdsMultiRoomComboPayload(rooms, options);

  const globalFlightDealId = resolveGlobalFlightLdsDealId(options);
  const extendedRows = extendedRowsFromOptions(options);
  const { sourcePath: extendedDealSourcePath } =
    resolveExtendedDealRowsForLdsMultiRoom(options);
  const availableDealIds = collectAvailableDealIds(extendedRows);
  const traces = [];
  const issues = [];

  rooms.forEach((slot, roomIndex) => {
    const room = roomInner(slot);
    const token =
      pickToken(room, ["ldsDealId", "LdsDealId"]) ||
      pickToken(slot, ["ldsDealId", "LdsDealId"]) ||
      pickToken(room, ["dealId", "DealId"]) ||
      "";
    const selectedRoomId =
      room["@seqNum"] ??
      room.roomId ??
      room.RoomId ??
      room.seqNum ??
      null;
    const roomName =
      room.roomType ??
      room.roomTypeName ??
      room.roomName ??
      slot?.roomType ??
      null;

    const trace = {
      roomIndex,
      extendedDealSourcePath: extendedDealSourcePath ?? null,
      packageDealId:
        options.selectedResult?.dealId != null
          ? String(options.selectedResult.dealId)
          : null,
      roomName: roomName != null ? String(roomName) : null,
      roomIdentifier:
        selectedRoomId != null
          ? String(selectedRoomId)
          : slot?.["@seqNum"] != null
            ? String(slot["@seqNum"])
            : null,
      selectedRoomId: selectedRoomId != null ? String(selectedRoomId) : null,
      roomType: pickToken(room, ["roomType", "roomTypeName", "roomName"]) || null,
      roomSet: pickToken(room, ["roomSet", "RoomSet", "room_set"]) || null,
      token: token || null,
      dealToken: token || null,
      tokenPresent: Boolean(token),
      isComboToken: token ? isRoomFlightComboToken(token) : false,
      equalsGlobalFlightDealId: Boolean(
        globalFlightDealId && token && token === globalFlightDealId
      ),
      availableDealIds,
      context: options.context ?? null,
    };
    traces.push(trace);

    if (!token) {
      issues.push({
        roomIndex,
        reason: "missing_per_room_combo_ldsDealId",
        ...trace,
      });
      return;
    }
    if (tokenEqualsGlobalFlightDealId(token, globalFlightDealId)) {
      issues.push({
        roomIndex,
        reason: "room_deal_equals_global_linkedFlights_ldsDealId",
        ...trace,
      });
      return;
    }
    if (isLdsJwtPackageDealToken(token)) {
      issues.push({
        roomIndex,
        reason: "token_lds_jwt_must_be_rebuilt_json_combo",
        ...trace,
      });
      return;
    }
    if (!isRoomFlightComboToken(token)) {
      issues.push({
        roomIndex,
        reason: "token_not_room_flight_combo",
        ...trace,
      });
    }
  });

  if (issues.length > 0) {
    return {
      ok: false,
      code: LDS_MULTI_ROOM_COMBO_DEAL_ID_MISSING,
      traces,
      issues,
    };
  }
  return { ok: true, code: null, traces, issues: [] };
}

export class LdsMultiRoomComboDealIdMissingError extends Error {
  constructor(message = LDS_MULTI_ROOM_COMBO_DEAL_ID_USER_MESSAGE_HE, details = {}) {
    super(message);
    this.name = "LdsMultiRoomComboDealIdMissingError";
    this.code = LDS_MULTI_ROOM_COMBO_DEAL_ID_MISSING;
    this.traces = details.traces ?? [];
    this.issues = details.issues ?? [];
  }
}

export function assertLdsMultiRoomComboDealIds(roomsAndPaxInfo, options = {}) {
  const result = validateLdsMultiRoomComboDealIds(roomsAndPaxInfo, options);
  if (!result.ok) {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      try {
        // eslint-disable-next-line no-console
        console.error("[LDS_MULTI_ROOM_COMBO_DEAL_ID_TRACE]", {
          ...result,
          issueReasons: result.issues?.map((i) => ({
            roomIndex: i.roomIndex,
            reason: i.reason,
            roomSet: i.roomSet,
            tokenPresent: i.tokenPresent,
          })),
        });
      } catch {
        /* ignore */
      }
    }
    throw new LdsMultiRoomComboDealIdMissingError(
      LDS_MULTI_ROOM_COMBO_DEAL_ID_USER_MESSAGE_HE,
      result
    );
  }
  return result;
}

/**
 * Ensures per-room combo tokens are preserved; never copies global flight ldsDealId onto rooms.
 */
export function applyFlightsHotelMultiRoomLdsDealIdSsot(
  roomsAndPaxInfo,
  selectedFlightOptionFromUser
) {
  const rooms = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo : [];
  if (rooms.length < 2) {
    return rooms;
  }
  const flightToken = pickToken(selectedFlightOptionFromUser, ["ldsDealId"]);
  const globalFlightDealId =
    pickToken(selectedFlightOptionFromUser?.linkedFlights, ["ldsDealId", "dealId"]) ||
    flightToken;
  // Multi-room: room combo tokens come from room selection only — not from linkedFlights.
  return rooms.map((slot) => {
    if (!slot || typeof slot !== "object") return slot;
    const inner = roomInner(slot);
    const existing =
      pickToken(inner, ["ldsDealId", "dealId"]) ||
      pickToken(slot, ["ldsDealId", "dealId"]);
    if (
      existing &&
      globalFlightDealId &&
      tokenEqualsGlobalFlightDealId(existing, globalFlightDealId)
    ) {
      const nextRoom = {
        ...(slot.room && typeof slot.room === "object" ? slot.room : {}),
      };
      delete nextRoom.ldsDealId;
      if (nextRoom.dealId === globalFlightDealId) delete nextRoom.dealId;
      const nextSlot = { ...slot, room: nextRoom };
      if (tokenEqualsGlobalFlightDealId(slot.ldsDealId, globalFlightDealId)) {
        delete nextSlot.ldsDealId;
      }
      if (tokenEqualsGlobalFlightDealId(slot.dealId, globalFlightDealId)) {
        delete nextSlot.dealId;
      }
      return nextSlot;
    }
    return slot;
  });
}
